import { useSyncExternalStore } from 'react'
import { inbox } from './api'
import { store } from './store'

/**
 * Optimistic sending. A reply shows up in its conversation the moment you send
 * it — "Sending in 10 s", then "Sent" — while the real message is still in the
 * undo window. When the provider's Sent copy syncs into the thread, the
 * placeholder quietly gives way to it. Undo removes it and puts the text back.
 */
export interface Pending {
  localId: string
  conversation: string
  operationId: string | null
  text: string
  to: string
  at: number
  /** When the undo window closes (null until the server has accepted the send). */
  sendAt: number | null
  status: 'queued' | 'scheduled' | 'sent' | 'failed'
}

let items: Pending[] = []
const listeners = new Set<() => void>()
const emit = () => { items = [...items]; for (const listener of listeners) listener() }
export const usePending = () => useSyncExternalStore(listener => { listeners.add(listener); return () => listeners.delete(listener) }, () => items)

/** Text whose send was undone, waiting to be put back in its reply box. */
const restored = new Map<string, string>()
export const takeRestored = (conversation: string) => { const text = restored.get(conversation); restored.delete(conversation); return text }

let me: Promise<string> | null = null
/** The name you send as (Settings > Your name), for the placeholder's header. */
export const myName = () => (me ??= fetch('/api/sender-name').then(r => r.json()).then((d: { name?: string }) => d.name || 'You').catch(() => 'You'))

export function addPending(input: { conversation: string; text: string; to: string }): string {
  const localId = crypto.randomUUID()
  items.push({ localId, conversation: input.conversation, operationId: null, text: input.text, to: input.to, at: Date.now(), sendAt: null, status: 'queued' })
  emit()
  return localId
}

export function acceptPending(localId: string, operationId: string, undoSeconds: number, sendAt?: string | null) {
  const item = items.find(p => p.localId === localId)
  if (!item) return
  item.operationId = operationId
  item.sendAt = sendAt ? Date.parse(sendAt) : Date.now() + undoSeconds * 1000
  item.status = item.sendAt > Date.now() + 300 ? 'scheduled' : 'sent'
  emit()
  watch(item)
}

export function failPending(localId: string) {
  items = items.filter(p => p.localId !== localId); emit()
}

/** Undo: drop the placeholder and give the text back to the reply box. */
export function cancelPending(operationId: string) {
  const item = items.find(p => p.operationId === operationId)
  if (!item) return
  restored.set(item.conversation, item.text)
  items = items.filter(p => p !== item); emit()
}

/** After the undo window: confirm with the server, then let the synced Sent copy replace the placeholder. */
function watch(item: Pending) {
  const check = async () => {
    if (!items.includes(item) || !item.operationId) return
    try {
      const op = await inbox.operation(item.operationId)
      if (op.status === 'succeeded' || op.status === 'partial') { item.status = 'sent'; emit(); store.soon(300); setTimeout(() => store.soon(0), 4_000) }
      else if (op.status === 'failed' || op.status === 'cancelled') { item.status = 'failed'; emit(); return }
      else { setTimeout(check, 1_500); return }
    } catch { setTimeout(check, 3_000); return }
  }
  setTimeout(check, Math.max(500, (item.sendAt ?? Date.now()) - Date.now() + 800))
  // Never leave a ghost: after two minutes, fall back to whatever the thread shows.
  setTimeout(() => { if (items.includes(item) && item.status === 'sent') { items = items.filter(p => p !== item); emit() } }, 120_000)
}

/** The real Sent copy has arrived in the conversation: retire matching placeholders. */
export function settleWith(conversation: string, sent: Array<{ preview: string; receivedAt: string }>) {
  const before = items.length
  items = items.filter(p => {
    if (p.conversation !== conversation || p.status !== 'sent') return true
    const start = p.text.replace(/\s+/g, ' ').trim().slice(0, 40).toLowerCase()
    return !sent.some(m => Date.parse(m.receivedAt) >= p.at - 120_000 && m.preview.replace(/\s+/g, ' ').trim().toLowerCase().startsWith(start.slice(0, 24)))
  })
  if (items.length !== before) emit()
}
